/**
 * PATH: apps/quote/src/components/builder/panels/kpi-summary-parser.ts
 * FILE_ID: QB-KPI-SUMMARY-PARSER-001
 * PURPOSE: Pure I→O parser — turn the free-form Quote.config blob into
 *          a strong-typed KPISummary, or null when no KPI data exists.
 * WHY: Splitting parsing out of the panel keeps the React component
 *      under 300 lines AND makes the parser unit-testable without
 *      pulling in React + lucide.
 *
 * DEPENDENCIES: none (pure JS)
 *
 * RELATED:
 *   - QB-KPI-SUMMARY-PANEL-001 (KPISummaryPanel.tsx) — consumer
 *   - QB-BRAVADA-DEMO-MAP-001 (BE bravada_demo_quote_mapping.py)
 *     populates the source `config.kpiSummary` keys.
 */

export interface KPIRow {
  label: string
  cost: number
  chargeout: number
}

/**
 * Adjusted GM block — what the Excel KPI Summary J-Q columns show.
 *
 * The .xlsm has two parallel KPI breakdowns:
 *   1. RAW (cols C-H, rows 11-17): per-discipline cost + chargeout rolled
 *      up directly from Scope & Pricing. Sum is the un-discounted
 *      chargeout total (~$9.4M on Q9525R9).
 *   2. ADJUSTED (cols J-Q, rows 20-23): re-bases the same costs against
 *      the contract value (B22 = ProjectTotal = $10,899,999.99). The
 *      delta between adjusted-revenue and raw-chargeout is the
 *      "margin creep" — the Night Shift line + Mobilisation lines that
 *      ride extra revenue without extra cost so the directors hit a
 *      round contract price.
 *
 * Excel cell references (KPI Summary sheet):
 *   adjustedTotal       = B22 = =ProjectTotal             ($10.9M)
 *   rawChargeout        = C17 = =SUM(C11:C15)             ($9.4M)
 *   marginCreep         = C22 = =+B22-C17                 ($1.5M)
 *   adjustedMargin      = D22 = =+F17+C22                 ($4.7M)
 *   adjustedMarginPct   = E22 = =IFERROR(D22/B22, 0)      (43%)
 *   rawMarginPct        = G17 = =IFERROR(F17/C17, 0)      (33%)
 *   pctIncrease         = E23 = =+E22-G17                 (10pp uplift)
 */
export interface AdjustedKPI {
  adjustedTotal: number
  rawChargeout: number
  marginCreep: number
  adjustedMargin: number
  adjustedMarginPct: number  // decimal (0.4314 = 43.14%)
  rawMarginPct: number       // decimal
  pctIncrease: number        // decimal (E22 - G17)
}

export interface KPISummary {
  rows: KPIRow[]
  totalLabourHours: number
  totalLabourDays: number
  quoteRevenueExclGst: number
  quoteGst: number
  quoteTotalInclGst: number
  discount: number
  adjusted: AdjustedKPI | null
}

export function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const n = Number.parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/**
 * Margin% from cost+chargeout pair. Zero chargeout returns 0 (no
 * margin defined) rather than dividing by zero.
 */
export function marginPercent(cost: number, chargeout: number): number {
  if (chargeout === 0) return 0
  return ((chargeout - cost) / chargeout) * 100
}

/**
 * Pull the KPI Summary slice out of the free-form config blob.
 * Returns null when the quote wasn't loaded via the Bravada demo
 * loader (i.e. no kpiSummary key, or every discipline-revenue is
 * zero) — callers render an empty state.
 *
 * Field-name contract MUST match the BE
 * `bravada_demo_quote_mapping.build_quote_config()` keys exactly.
 */
export function parseKpiSummary(config: unknown): KPISummary | null {
  if (!config || typeof config !== 'object') return null
  const root = config as Record<string, unknown>
  const k = root.kpiSummary
  if (!k || typeof k !== 'object') return null
  const kpi = k as Record<string, unknown>

  // Treat any KPI block where ALL discipline-revenue fields are zero
  // as "no data" — saves the panel from rendering a useless table of
  // dashes for quotes the Bravada loader hasn't touched.
  const fields = [
    'labourEstRevenue',
    'productEstRevenue',
    'plantEstRevenue',
    'subconEstRevenue',
    'otherChargesChargeOut',
  ]
  const hasData = fields.some(f => toNumber(kpi[f]) !== 0)
  if (!hasData) return null

  const rows: KPIRow[] = [
    {
      label: 'Labour',
      cost: toNumber(kpi.labourEstBudget),
      chargeout: toNumber(kpi.labourEstRevenue),
    },
    {
      label: 'Products',
      cost: toNumber(kpi.productEstBudget),
      chargeout: toNumber(kpi.productEstRevenue),
    },
    {
      label: 'Plant',
      cost: toNumber(kpi.plantEstBudget),
      chargeout: toNumber(kpi.plantEstRevenue),
    },
    {
      label: 'Subcontractors',
      cost: toNumber(kpi.subconEstBudget),
      chargeout: toNumber(kpi.subconEstRevenue),
    },
    {
      label: 'Other Charges',
      cost: toNumber(kpi.otherChargesCosts),
      chargeout: toNumber(kpi.otherChargesChargeOut),
    },
  ]

  const quoteRevenueExclGst = toNumber(kpi.quoteRevenueExclGst)
  return {
    rows,
    totalLabourHours: toNumber(kpi.totalLabourHours),
    totalLabourDays: toNumber(kpi.totalLabourDays),
    quoteRevenueExclGst,
    quoteGst: toNumber(kpi.quoteGst),
    quoteTotalInclGst: toNumber(kpi.quoteTotalInclGst),
    discount: toNumber(kpi.discount),
    // Adjusted GM block — mirrors KPI Summary cells J20-Q23.
    // Returned `null` when we don't have a non-zero contract value to
    // re-base the costs against (defensive: an old quote without an
    // imported `quoteRevenueExclGst` would otherwise show a misleading
    // "100% margin" because adjustedTotal would be 0).
    adjusted: computeAdjustedKpi(rows, quoteRevenueExclGst),
  }
}

/**
 * Compute the adjusted GM block from the raw rows + the contract value.
 *
 * Returns null when the contract value (= B22 in Excel) is unknown — the
 * panel renders an empty state rather than showing a 100%-margin
 * artefact. This is a pure function so it's trivially unit testable.
 */
export function computeAdjustedKpi(
  rows: KPIRow[],
  contractValue: number,
): AdjustedKPI | null {
  if (contractValue <= 0) return null

  const totalCost = rows.reduce((s, r) => s + r.cost, 0)
  const rawChargeout = rows.reduce((s, r) => s + r.chargeout, 0)

  // Margin creep = how much MORE revenue the contract carries vs. the
  // raw rolled-up chargeouts. On Bravada this is the Night Shift +
  // Mobilisation "premiums" that the directors keep parked outside the
  // discipline breakdown. Can be negative if the contract was
  // discounted below cost+markup — surface it either way.
  const marginCreep = contractValue - rawChargeout
  const adjustedMargin = contractValue - totalCost
  const adjustedMarginPct = adjustedMargin / contractValue
  const rawMarginPct =
    rawChargeout > 0 ? (rawChargeout - totalCost) / rawChargeout : 0

  return {
    adjustedTotal: contractValue,
    rawChargeout,
    marginCreep,
    adjustedMargin,
    adjustedMarginPct,
    rawMarginPct,
    pctIncrease: adjustedMarginPct - rawMarginPct,
  }
}
